import type {ReactElement} from 'react';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import styles from './pill.module.scss';
import {Text, TextSize, TextType} from '../text';
import {Icon} from '../icon-v2';
import {noop} from '@Utils/general.util';

export interface PillProps {
  content: PillContent;
  className?: string;
  size?: PILL_SIZE;
  type?: PILL_TYPE;
  isFlat?: boolean;
  isDisabled?: boolean;
  background?: PILL_BACKGROUND_COLOR | string;
  theme?: PILL_THEME;
  isActive?: boolean;
  isHoverable?: boolean;

  onClick?(): void;

  onMouseEnter?(): void;

  onMouseLeave?(): void;

  'data-testid'?: string;
}

export enum PILL_THEME {
  NEUTRAL = 'NEUTRAL',
  PRIMARY = 'PRIMARY',
  SUCCESS = 'SUCCESS',
  DANGER = 'DANGER',
  PREMIUM = 'PREMIUM',
  SECONDARY = 'SECONDARY',
  AI = 'AI',
}

export enum PILL_BACKGROUND_COLOR {
  PRIMARY_10 = 'PRIMARY_10',
  PRIMARY_20 = 'PRIMARY_20',
  PREMIUM_10 = 'PREMIUM_10',
  PREMIUM_20 = 'PREMIUM_20',
  SUCCESS_10 = 'SUCCESS_10',
  SECONDARY_10 = 'SECONDARY_10',
  SECONDARY_20 = 'SECONDARY_20',
  SECONDARY_100 = 'SECONDARY_100',
  DANGER_10 = 'DANGER_10',
  NEUTRAL_3 = 'NEUTRAL_3',
  TRANSPARENT = 'TRANSPARENT',
  NONE = 'NONE',
}

export enum PILL_SIZE {
  XSMALL = 'xsmall',
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export enum PILL_TYPE {
  ROUND = 'round',
  RECTANGLE = 'rectangle',
}

export type PillContent = IconContent | TextContent | HybridContent;

interface IconContent extends BasePillContent {
  type: PILL_CONTENT_TYPE.ICON;
  icon: string;
  size: IconSize;
}

interface TextContent extends BasePillContent {
  type: PILL_CONTENT_TYPE.TEXT;
  text: string;
  textClasses?: string;
  textColor?: string;
  textSize?: TextSize;
  isBold?: boolean;
}

interface HybridContent extends BasePillContent {
  type: PILL_CONTENT_TYPE.HYBRID;
  textSize?: TextSize;
  text: string;
  textColor?: string;
  isBold?: boolean;
  icon: string;
  iconColor?: string;
  iconClasses?: string;
  gap?: number;
  textClasses?: string;
  iconSize?: IconSize;
  iconTestId?: string;
}

interface BasePillContent {
  type: PILL_CONTENT_TYPE;
  classname?: string;
}

export enum PILL_CONTENT_TYPE {
  ICON = 'icon',
  TEXT = 'text',
  HYBRID = 'hybrid',
}

const DEFAULT_PILL_GAP = 4;

export function Pill({
  size = PILL_SIZE.MEDIUM,
  className = '',
  onClick = noop,
  onMouseEnter = noop,
  onMouseLeave = noop,
  isFlat = true,
  isDisabled = false,
  background = PILL_BACKGROUND_COLOR.PRIMARY_10,
  theme = undefined,
  type = PILL_TYPE.ROUND,
  isActive = false,
  isHoverable = false,
  ...props
}: PillProps): ReactElement<any> {
  const getSizeClasses = (): string => {
    if (size === PILL_SIZE.XSMALL) {
      return styles.xsmall;
    }
    if (size === PILL_SIZE.SMALL) {
      return styles.small;
    }
    if (size === PILL_SIZE.MEDIUM) {
      return styles.medium;
    }
    if (size === PILL_SIZE.LARGE) {
      return styles.large;
    }
    return '';
  };

  const getPillTypeClasses = (): string => {
    switch (type) {
      case PILL_TYPE.ROUND:
        return styles.round;
      case PILL_TYPE.RECTANGLE:
        return styles.rectangle;
      default:
        return '';
    }
  };

  const getThemeClasses = (): string => {
    if (typeof theme === 'undefined') {
      return '';
    }

    switch (theme) {
      case PILL_THEME.NEUTRAL:
        return styles.themeNeutral;
      case PILL_THEME.PRIMARY:
        return styles.themePrimary;
      case PILL_THEME.SUCCESS:
        return styles.themeSuccess;
      case PILL_THEME.DANGER:
        return styles.themeDanger;
      case PILL_THEME.PREMIUM:
        return styles.themePremium;
      case PILL_THEME.SECONDARY:
        return styles.themeSecondary;
      case PILL_THEME.AI:
        return styles.themeAi;
      default:
        return '';
    }
  };

  const getBackgroundColorClasses = (): string => {
    if (isDisabled) {
      return styles.neutral3;
    }
    if (isFlat) {
      switch (background) {
        case PILL_BACKGROUND_COLOR.PRIMARY_20:
          return styles.primary20;
        case PILL_BACKGROUND_COLOR.PREMIUM_10:
          return styles.premium10;
        case PILL_BACKGROUND_COLOR.PREMIUM_20:
          return styles.premium20;
        case PILL_BACKGROUND_COLOR.SUCCESS_10:
          return styles.success10;
        case PILL_BACKGROUND_COLOR.SECONDARY_10:
          return styles.secondary10;
        case PILL_BACKGROUND_COLOR.SECONDARY_20:
          return styles.secondary20;
        case PILL_BACKGROUND_COLOR.SECONDARY_100:
          return styles.secondary100;
        case PILL_BACKGROUND_COLOR.DANGER_10:
          return styles.danger10;
        case PILL_BACKGROUND_COLOR.NEUTRAL_3:
          return styles.neutral3;
        case PILL_BACKGROUND_COLOR.TRANSPARENT:
          return styles.transparent;
        case PILL_BACKGROUND_COLOR.PRIMARY_10:
          return styles.primary10;
        case PILL_BACKGROUND_COLOR.NONE:
          return '';
        default:
          return background;
      }
    }
    return styles.pillBackground;
  };

  const getPillClasses = (): string => {
    return [className, styles.container, getSizeClasses(), getBackgroundColorClasses(), getPillTypeClasses(), getThemeClasses(), getActiveClass(), getHoverClasses()].join(' ');
  };

  const getActiveClass = (): string => {
    return isActive ? styles.isActive : '';
  };

  const getHoverClasses = (): string => {
    if (typeof theme === 'undefined' || isHoverable === false) {
      return '';
    }

    switch (theme) {
      case PILL_THEME.NEUTRAL:
        return styles.neutralHover;
      case PILL_THEME.PRIMARY:
        return styles.primaryHover;
      case PILL_THEME.SUCCESS:
        return styles.successHover;
      case PILL_THEME.DANGER:
        return styles.dangerHover;
      case PILL_THEME.PREMIUM:
        return styles.premiumHover;
      default:
        return '';
    }
  };

  const displayContent = (): ReactElement<any> | null => {
    if (props.content.type === PILL_CONTENT_TYPE.ICON) {
      return <Icon icon={props.content.icon} size={props.content.size} className={`${styles.pillText}`} isDisabled />;
    }
    if (props.content.type === PILL_CONTENT_TYPE.TEXT) {
      return (
        <Text
          val={props.content.text}
          size={props.content.textSize ?? TextSize.XXSMALL}
          className={`${styles.pillText} ${isDisabled ? 'content-disabled' : (props.content.textColor ?? 'content-primary')} ${props.content.textClasses as string}`}
          bold={props.content.isBold ?? true}
        />
      );
    }
    if (props.content.type === PILL_CONTENT_TYPE.HYBRID) {
      return (
        <div className="flex-center" style={{gap: props.content.gap ?? DEFAULT_PILL_GAP}}>
          <Icon
            icon={props.content.icon}
            className={`${props.content.iconClasses} ${isDisabled ? 'content-disabled' : (props.content.iconColor ?? (props.content.iconClasses ? '' : 'content-body'))}`}
            size={props.content.iconSize ?? IconSize.SIZE_ICON_16}
            data-testid={props.content.iconTestId}
          />
          <Text
            val={props.content.text}
            size={props.content.textSize ?? TextSize.XSMALL}
            bold={props.content.isBold}
            type={TextType.BODY}
            className={`${styles.pillText} ${isDisabled ? 'content-disabled' : (props.content.textColor ?? 'content-body')} ${props.content.textClasses}`}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <ClickableDiv onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} className={`${getPillClasses()}`} onClick={onClick} data-testid={props['data-testid']}>
      {displayContent()}
    </ClickableDiv>
  );
}
